import { Spin } from "antd";
import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import moment from "moment";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from "react";
import { AUDIT_PROJECT_VENDOR_EVENTS } from "admin-app/services/ciq-queries";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import AuditTimeline, {
  type TAuditTimelineItem
} from "../components/audit-timeline";
import type {
  TProjectVendorMasterRow,
  TProjectVendorRow
} from "../hooks/entity-audit-data";

type TProjectVendorAuditEvent = {
  event_log_id: string;
  field: string | null;
  new_bool: boolean | null;
  new_string: string | null;
  old_bool: boolean | null;
  old_string: string | null;
  created_by: string | null;
  created_at: string | null;
  created_by_user: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
  } | null;
  event_log: {
    source: string | null;
    event: {
      name: string | null;
      description: string | null;
    } | null;
  } | null;
};

function formatPersonName(
  user: {
    first_name: string | null;
    last_name: string | null;
  } | null
): string {
  if (!user) return "";
  return `${user.first_name || ""} ${user.last_name || ""}`.trim();
}

function formatActorDisplayName(eventRecord: TProjectVendorAuditEvent): string {
  const user = eventRecord.created_by_user;
  const fullName = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
  if (fullName) {
    return user?.email ? `${fullName} (${user.email})` : fullName;
  }
  if (user?.email) {
    return user.email;
  }
  return eventRecord.created_by || "Unknown";
}

function buildVendorTimelineItems(
  records: TProjectVendorAuditEvent[]
): TAuditTimelineItem[] {
  const groupedRecords: Record<string, TProjectVendorAuditEvent[]> = {};

  for (let index = 0; index < records.length; index += 1) {
    const record = records[index];
    const groupId = record.event_log_id || `event-${index}`;
    if (!groupedRecords[groupId]) {
      groupedRecords[groupId] = [];
    }
    groupedRecords[groupId].push(record);
  }

  return Object.entries(groupedRecords)
    .map(([groupId, groupItems]) => {
      const firstItem = groupItems[0];
      return {
        id: groupId,
        timestamp: firstItem?.created_at || null,
        label:
          firstItem?.event_log?.event?.description ||
          firstItem?.event_log?.event?.name ||
          "Project company event",
        description: `By: ${formatActorDisplayName(firstItem)}${
          firstItem?.event_log?.source
            ? ` | Source: ${firstItem.event_log.source}`
            : ""
        }`,
        changes: groupItems.map((eventItem) => {
          const oldValue =
            eventItem.old_string ??
            (eventItem.old_bool !== null ? `${eventItem.old_bool}` : null);
          const newValue =
            eventItem.new_string ??
            (eventItem.new_bool !== null ? `${eventItem.new_bool}` : null);
          return {
            field: eventItem.field || "field",
            fromValue: oldValue,
            toValue: newValue
          };
        })
      };
    })
    .sort((firstItem, secondItem) => {
      const firstTime = firstItem.timestamp
        ? moment(firstItem.timestamp).valueOf()
        : 0;
      const secondTime = secondItem.timestamp
        ? moment(secondItem.timestamp).valueOf()
        : 0;
      return secondTime - firstTime;
    });
}

function ProjectVendorTimelineDetailRenderer(props: {
  data?: TProjectVendorMasterRow;
}) {
  const projectId = props?.data?.projectVendor?.project_id;
  const vendorId = props?.data?.projectVendor?.vendor_id;
  const { data, loading } = useCIQQuery<{
    audit_project_events: TProjectVendorAuditEvent[];
  }>(AUDIT_PROJECT_VENDOR_EVENTS, {
    variables: {
      project_id: projectId,
      vendor_id: vendorId,
      vendor_id_string: vendorId
    },
    skip: !projectId || !vendorId
  });

  const timelineItems = useMemo(
    () => buildVendorTimelineItems(data?.audit_project_events || []),
    [data?.audit_project_events]
  );

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-md p-4 my-2 mx-3 min-h-[120px] flex items-center justify-center">
        <Spin size="small" />
      </div>
    );
  }

  return (
    <AuditTimeline
      items={timelineItems}
      emptyText="No project company audit events available."
    />
  );
}

export type TProjectCompaniesAuditGridProps = {
  loading: boolean;
  filteredRows: TProjectVendorRow[];
};

export type TProjectCompaniesAuditGridHandle = {
  setQuickFilterText: (text: string) => void;
};

const ProjectCompaniesAuditGrid = forwardRef<
  TProjectCompaniesAuditGridHandle,
  TProjectCompaniesAuditGridProps
>(function ProjectCompaniesAuditGrid(props, ref) {
  const { loading, filteredRows } = props;
  const gridRef = useRef<AgGridReact<TProjectVendorMasterRow>>(null);

  useImperativeHandle(ref, () => ({
    setQuickFilterText: (text: string) => {
      gridRef.current?.api?.setGridOption("quickFilterText", text);
    }
  }));

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      flex: 1,
      minWidth: 160
    }),
    []
  );

  const columnDefs = useMemo<ColDef<TProjectVendorMasterRow>[]>(() => {
    return [
      {
        colId: "groupTitle",
        field: "groupTitle",
        headerName: "Company",
        cellRenderer: "agGroupCellRenderer",
        flex: 1,
        minWidth: 200
      },
      {
        colId: "roleName",
        field: "roleName",
        headerName: "Type ID",
        flex: 0,
        width: 120,
        minWidth: 100
      },
      {
        colId: "statusName",
        field: "statusName",
        headerName: "Status ID",
        flex: 0,
        width: 140,
        minWidth: 120
      },
      {
        colId: "createdBy",
        field: "createdBy",
        headerName: "Created By",
        flex: 0,
        width: 160,
        minWidth: 140
      },
      {
        colId: "createdAt",
        field: "createdAt",
        headerName: "Created At",
        valueGetter: ({ data }) =>
          data?.createdAt ? moment(data.createdAt).format("MM-DD-YYYY") : "",
        flex: 0,
        width: 130,
        minWidth: 120
      },
      {
        colId: "updatedBy",
        field: "updatedBy",
        headerName: "Updated By",
        flex: 0,
        width: 160,
        minWidth: 140
      },
      {
        colId: "updatedAt",
        field: "updatedAt",
        headerName: "Updated At",
        sort: "asc",
        valueGetter: ({ data }) =>
          data?.updatedAt ? moment(data.updatedAt).format("MM-DD-YYYY") : "",
        flex: 0,
        width: 130,
        minWidth: 120
      }
    ];
  }, []);

  useEffect(() => {
    gridRef.current?.api?.setGridOption("loading", loading);
  }, [loading]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) {
      return;
    }

    api.applyColumnState({
      state: [{ colId: "updatedAt", sort: "asc" }],
      defaultState: { sort: null }
    });
  }, []);

  const rowData = useMemo<TProjectVendorMasterRow[]>(() => {
    return filteredRows.map((row) => {
      const displayName = row.vendor_name?.trim() || row.vendor_id;
      return {
        groupKey: `${row.id}`,
        groupTitle: displayName,
        roleName:
          row.type_id !== null && row.type_id !== undefined
            ? `${row.type_id}`
            : "",
        statusName:
          row.status !== null && row.status !== undefined
            ? `${row.status}`
            : "",
        createdBy: formatPersonName(row.created_by_user),
        createdAt: row.created_at,
        updatedBy: formatPersonName(row.updated_by_user),
        updatedAt: row.updated_at,
        projectVendor: row
      };
    });
  }, [filteredRows]);

  return (
    <AgGridReact<TProjectVendorMasterRow>
      ref={gridRef}
      rowData={rowData}
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      suppressContextMenu
      masterDetail
      isRowMaster={() => true}
      detailCellRenderer={ProjectVendorTimelineDetailRenderer}
      detailRowHeight={380}
      getRowId={(params) => params.data.groupKey}
      animateRows
    />
  );
});

export default ProjectCompaniesAuditGrid;
